import { DepositStep, CommonModalsKey } from '../../enums';
import { Deposit } from '../../models';
import { useModalNavigation } from '../modals/useModalNavigation';
import { useSearchParams } from '../useSearchParams';

/**
 * Custom hook to handle deposit callbacks.
 * This hook provides a function to handle successful deposit operations,
 * updating the URL search parameters and optionally redirecting externally.
 *
 * @param {boolean} [externalRedirect] - Optional flag to determine if an external redirect should occur.
 * @returns {Object} An object containing the onSuccess function.
 *
 * @example
 * const { onSuccess } = useDepositCallback(true);
 * onSuccess(deposit);
 */
export function useDepositCallback(externalRedirect?: boolean) {
  // Retrieve the current URL search parameters
  const searchParams: URLSearchParams = useSearchParams();
  // Destructure the navigateToModal function from useModalNavigation hook
  const { navigateToModal } = useModalNavigation();

  /**
   * Function to handle successful deposit operations.
   * It updates the URL search parameters with the deposit ID and step,
   * and navigates to the deposit modal. Optionally, it can open an external link.
   *
   * @param {Deposit} deposit - The deposit object containing details of the successful deposit.
   *
   * @example
   * const deposit = { id: 123, paymentLink: { url: 'https://example.com' } };
   * onSuccess(deposit);
   */
  function onSuccess(deposit: Deposit) {
    // Check if external redirect is required
    if (externalRedirect) {
      setTimeout(() => {
        // Open the payment link in a new tab
        window.open(deposit?.paymentLink?.url, '_blank');
      });
    }

    // Create a new URLSearchParams object from the current search parameters
    const newUrlSearchParams = new URLSearchParams(searchParams);
    // Set the deposit ID in the search parameters
    newUrlSearchParams.set('deposit-id', deposit.id.toString());
    // Set the step to 'Response' in the search parameters
    newUrlSearchParams.set('step', DepositStep.Response);

    // Navigate to the deposit modal with the updated search parameters
    navigateToModal(
      CommonModalsKey.Deposit,
      `?${newUrlSearchParams.toString()}`,
    );
  }

  // Return the onSuccess function
  return {
    onSuccess,
  };
}
